import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, FileDown, Undo2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { Btn, Card, Pill, Sel, Bar, Head, errMsg, download, usePoll, useMeta, ENTITY_OPTS, DC } from "@/components/datacenter/ui";
import MappingStep from "@/components/datacenter/MappingStep";
import BehaviorStep from "@/components/datacenter/BehaviorStep";
import PreviewStep from "@/components/datacenter/PreviewStep";

const STEPS = ["Data type", "Upload", "Analyze", "Map fields", "Preview", "Behavior", "Validate", "Import", "Results"];
const stepOf = (stage) => ({ analyzing: 2, validating: 6, validated: 4, importing: 7, completed: 8, rolled_back: 8, failed: 8 }[stage] ?? 1);

function Steps({ at }) {
  return (
    <div className="flex gap-1 flex-wrap mb-5" data-testid="dc-steps">
      {STEPS.map((s, i) => <span key={s} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${i < at ? "bg-[#165a71]/10 text-[#165a71]" : i === at ? "bg-[#071b22] text-white" : "bg-slate-100 text-slate-500"}`}>{i + 1}. {s}</span>)}
    </div>
  );
}

function Upload({ base, admin, onCreated }) {
  const [sp] = useSearchParams();
  const [entity, setEntity] = useState(sp.get("entity") || "providers");
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const fd = new FormData();
    if (admin) fd.append("entity", entity);
    if (file) fd.append("file", file); else fd.append("text", text);
    setBusy(true);
    try { const r = await api.post(base, fd); onCreated(r.data.id); }
    catch (e) { toast.error(errMsg(e, "Upload failed")); } finally { setBusy(false); }
  };
  return (
    <Card tid="dc-upload-step">
      <Steps at={file || text ? 2 : admin ? 0 : 1} />
      {admin && (
        <div className="mb-5">
          <div className="text-sm font-semibold mb-2">1. What are you importing?</div>
          <div className="flex gap-2">{ENTITY_OPTS.map(([k, l]) => <button key={k} onClick={() => setEntity(k)} className={`rounded-2xl border px-5 py-3 text-sm font-medium ${entity === k ? "border-[#165a71] bg-[#165a71]/5 text-[#165a71]" : "border-slate-200"}`} data-testid={`dc-entity-${k}`}>{l}</button>)}</div>
        </div>
      )}
      <div className="text-sm font-semibold mb-2">{admin ? "2. " : ""}Upload a CSV or paste data</div>
      <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 p-8 cursor-pointer hover:border-[#165a71]/50 bg-slate-50/50" data-testid="dc-file-drop">
        <UploadCloud className="h-8 w-8 text-[#165a71]" />
        <span className="text-sm">{file ? file.name : "Choose a .csv file (UTF-8; comma, semicolon, tab or pipe). Excel .xlsx also works."}</span>
        <input type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="dc-file-input" />
      </label>
      <div className="text-center text-xs text-[#252A34]/50 my-3">or paste CSV</div>
      <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} disabled={!!file} placeholder={"Business Name,Phone,City\nEmerald Coast Cleaning,850-555-1234,Panama City Beach"} className="w-full rounded-2xl border border-slate-200 p-3 text-sm font-mono" data-testid="dc-paste" />
      <div className="mt-4 flex justify-between items-center">
        {admin ? <Link to={`${DC}/templates`} className="text-sm text-[#165a71] underline">Download a template</Link> : <span />}
        <Btn onClick={go} busy={busy} disabled={!file && !text.trim()} tid="dc-analyze">Analyze file</Btn>
      </div>
    </Card>
  );
}

function Analysis({ job, meta }) {
  const s = job.summary || {};
  const stat = (k, l, tone) => <div className={`rounded-xl border p-3 ${tone}`} data-testid={`dc-sum-${k}`}><div className="text-xl font-display font-semibold">{(s[k] || 0).toLocaleString()}</div><div className="text-[11px] uppercase tracking-wide">{l}</div></div>;
  return (
    <Card tid="dc-analysis">
      <div className="flex justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-lg font-semibold text-[#071b22]" data-testid="dc-file-name">{job.file.name}</div>
          <div className="text-xs text-[#252A34]/60">{job.file.rows.toLocaleString()} rows · {job.file.columns} columns · delimiter “{job.file.delimiter}” · {job.file.encoding} · importing <b>{meta.entities[job.entity].label}</b>
            {job.estimated_entity !== job.entity && <span className="text-amber-700"> (file looks like {job.estimated_entity})</span>}</div>
          <div className="mt-2 flex flex-wrap gap-1">{job.headers.map((h) => <span key={h} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px]">{h}</span>)}</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
        {stat("total", "rows detected", "border-slate-200")}{stat("ready", "ready", "border-emerald-200 text-emerald-800")}{stat("incomplete", "incomplete but importable", "border-sky-200 text-sky-800")}
        {stat("dup_rows", "probable duplicates", "border-violet-200 text-violet-800")}{stat("review", "require review", "border-orange-200 text-orange-800")}{stat("invalid", "invalid", "border-rose-200 text-rose-800")}
      </div>
      <div className="mt-2 text-xs text-[#252A34]/60">{s.warning || 0} with warnings · will create {s.will_create || 0} · update {(s.will_update || 0) + (s.will_merge || 0)} · skip {s.will_skip || 0}</div>
    </Card>
  );
}

function Results({ job, base, admin, reload }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const c = job.counts || {};
  const doRollback = async (force) => {
    setBusy(true);
    try { const r = await api.post(`${base}/${job.id}/rollback`, { force }); toast.success(`Rolled back: ${r.data.removed} removed, ${r.data.restored} restored`); setPlan(null); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card tid="dc-results">
      <div className="flex items-center gap-2 font-display text-lg font-semibold text-[#071b22]"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> {job.stage === "rolled_back" ? "Import rolled back" : job.stage === "failed" ? "Import failed" : "Import complete"}</div>
      {job.error && <div className="text-sm text-rose-700 mt-1">{job.error}</div>}
      <div className="mt-3 flex flex-wrap gap-2 text-sm" data-testid="dc-result-counts">
        <Pill s="created">{c.created || 0} created</Pill><Pill s="updated">{(c.updated || 0) + (c.merged || 0)} updated</Pill><Pill s="skipped">{c.skipped || 0} skipped</Pill><Pill s="error">{c.errors || 0} errors</Pill>
        {c.auto_created_providers ? <Pill s="incomplete">{c.auto_created_providers} providers auto-created</Pill> : null}
      </div>
      {job.rollback && <div className="text-xs mt-2 text-[#252A34]/60">Rollback: {job.rollback.removed} removed · {job.rollback.restored} restored · {job.rollback.kept_modified} kept (changed after import)</div>}
      <div className="mt-4 flex gap-2 flex-wrap">
        <Btn kind="ghost" onClick={() => download("get", `${base}/${job.id}/errors.csv`, null, "error_report.csv")} tid="dc-error-report"><FileDown className="h-4 w-4" /> Error report.csv</Btn>
        {admin && <Link to={`${DC}/records?entity=${job.entity}`} className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm" data-testid="dc-view-records">Review & bulk edit records</Link>}
        {admin && job.stage === "completed" && <Btn kind="danger" onClick={() => api.get(`${base}/${job.id}/rollback-plan`).then((r) => setPlan(r.data)).catch((e) => toast.error(errMsg(e)))} tid="dc-rollback"><Undo2 className="h-4 w-4" /> Rollback import</Btn>}
      </div>
      {plan && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50/50 p-4 text-sm" data-testid="dc-rollback-plan">
          <div><b>{plan.counts.remove}</b> records to remove{plan.auto_created_providers ? ` (+${plan.auto_created_providers} auto-created providers)` : ""} · <b>{plan.counts.restore}</b> updated records to restore</div>
          {plan.counts.modified_after > 0 && <div className="mt-1 text-rose-700"><b>{plan.counts.modified_after}</b> records were changed after this import: {plan.modified_after.slice(0, 5).map((m) => m.name).join(", ")}. They are kept unless you force rollback.</div>}
          <div className="mt-3 flex gap-2">
            <Btn kind="danger" busy={busy} onClick={() => doRollback(false)} tid="dc-rollback-confirm">Roll back{plan.counts.modified_after ? " (keep changed records)" : ""}</Btn>
            {plan.counts.modified_after > 0 && <Btn kind="ghost" busy={busy} onClick={() => doRollback(true)} tid="dc-rollback-force">Force — overwrite later changes</Btn>}
            <Btn kind="ghost" onClick={() => setPlan(null)} tid="dc-rollback-cancel">Cancel</Btn>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function ImportWizard({ base = `${DC}/imports`, route = `${DC}/import`, admin = true }) {
  const { id } = useParams();
  const nav = useNavigate();
  const meta = useMeta(admin ? `${DC}/meta` : "/provider-import/meta");
  const [job, setJob] = useState(null);
  const [sample, setSample] = useState(null);
  const [tick, setTick] = useState(0);
  const load = () => id && api.get(`${base}/${id}`).then((r) => setJob(r.data)).catch(() => toast.error("Import not found"));
  useEffect(() => { setJob(null); load(); if (id) api.get(`${base}/${id}/rows`, { params: { size: 10 } }).then((r) => setSample(r.data.items?.[0]?.raw)); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const busy = job && ["analyzing", "validating", "importing"].includes(job.stage);
  usePoll(async () => { const r = await api.get(`${base}/${id}`); setJob(r.data); if (!["analyzing", "validating", "importing"].includes(r.data.stage)) setTick((t) => t + 1); }, busy);
  if (!meta) return <div className="text-[#252A34]/60">Loading…</div>;
  if (meta.error && admin) return <div className="text-rose-700">You don't have access to the Data Center.</div>;
  const metaFor = meta.entities ? meta : null;
  const confirm = async () => {
    try { await api.post(`${base}/${job.id}/commit`); toast.success("Import started — it runs in the background; you can leave this page"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5 max-w-6xl" data-testid="dc-import-wizard">
      <Head title={admin ? "Import data" : "Import services"} sub="Incomplete records are welcome — anything with enough information to make a usable draft is imported, and the rest can be completed later.">
        {id && <Btn kind="ghost" onClick={() => nav(route)} tid="dc-new-import">New import</Btn>}
      </Head>
      {!id && <Upload base={base} admin={admin} onCreated={(jid) => nav(`${route}/${jid}`)} />}
      {id && !job && <div className="text-[#252A34]/60">Loading…</div>}
      {job && metaFor && (
        <>
          <Card><Steps at={stepOf(job.stage)} />
            {busy && <div className="grid gap-3 sm:grid-cols-4" data-testid="dc-progress">
              <Bar label="Analyzing" pct={job.progress?.analyzing} /><Bar label="Mapping" pct={job.progress?.mapping} /><Bar label="Validating" pct={job.stage === "analyzing" ? 0 : job.progress?.validating ?? 100} /><Bar label="Importing" pct={job.progress?.importing} />
            </div>}
            {!busy && <div className="text-sm text-[#252A34]/70">Status: <Pill s={job.stage === "validated" ? "ready" : job.stage === "failed" ? "error" : "created"} tid="dc-job-stage">{job.stage.replace("_", " ")}</Pill></div>}
          </Card>
          {job.summary?.total !== undefined && <Analysis job={job} meta={metaFor} />}
          {job.stage === "validated" && <MappingStep key={`m${tick}`} job={job} meta={metaFor} base={base} sample={sample} admin={admin} onChanged={load} />}
          {job.stage === "validated" && <BehaviorStep key={`b${tick}`} job={job} meta={metaFor} base={base} admin={admin} onChanged={load} />}
          {job.summary?.total !== undefined && !busy && <PreviewStep job={job} meta={metaFor} base={base} admin={admin} refreshKey={tick} onChanged={load} />}
          {job.stage === "validated" && (
            <Card className="flex items-center justify-between flex-wrap gap-3" tid="dc-confirm">
              <div className="text-sm">Ready to import <b>{((job.summary.will_create || 0) + (job.summary.will_update || 0) + (job.summary.will_merge || 0)).toLocaleString()}</b> records. Rows that are invalid or still need review are skipped — the rest import even if some rows have problems.</div>
              <Btn kind="accent" onClick={confirm} tid="dc-commit">Import records</Btn>
            </Card>
          )}
          {["completed", "rolled_back", "failed"].includes(job.stage) && <Results job={job} base={base} admin={admin} reload={load} />}
        </>
      )}
    </div>
  );
}
